/**
 * Too GooD smooth scroll.
 *
 * One scrolling system for the whole site: Lenis (assets/lenis.min.js)
 * interpolates the real document scroll on wheel input so every section,
 * sticky rail, pinned chapter and scroll-linked effect reads the same
 * eased position. Touch stays native: iOS and Android momentum already
 * feel right, and fighting them is what makes "smooth scroll" feel wrong
 * on phones.
 *
 *  - When GSAP ScrollTrigger is on the page (the homepage manifesto), Lenis
 *    is driven by the GSAP ticker and ScrollTrigger.update runs on every
 *    Lenis tick, so pins and scrubs never see a stale position.
 *  - Same-page anchor links (#journey, #sets, the time rail) scroll through
 *    Lenis with the glass header offset, replacing the native smooth jump.
 *  - Lenis stops while the theme locks scroll for a drawer (html[scroll-lock])
 *    and ignores wheel input inside dialogs and elements marked
 *    data-lenis-prevent, so cart and menu drawers scroll natively.
 *  - Respects prefers-reduced-motion and the theme editor: no Lenis, native
 *    scroll, nothing attached.
 *
 * Exposed as window.tgLenis for other components.
 */
(() => {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const designMode = Boolean(window.Shopify && window.Shopify.designMode);
  const Lenis = window.Lenis;
  if (reduced || designMode || typeof Lenis !== 'function') return;

  const lenis = new Lenis({
    autoRaf: false,
    smoothWheel: true,
    syncTouch: false,
    duration: 1.15,
    easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
    wheelMultiplier: 1,
    lerp: 0.1,
    prevent: (node) =>
      node.tagName === 'DIALOG' ||
      (typeof node.hasAttribute === 'function' && node.hasAttribute('data-lenis-prevent')),
  });
  window.tgLenis = lenis;

  // Drive Lenis from the GSAP ticker when present, else our own loop.
  const wire = () => {
    const { gsap, ScrollTrigger } = window;
    if (gsap && ScrollTrigger) {
      lenis.on('scroll', ScrollTrigger.update);
      gsap.ticker.add((time) => lenis.raf(time * 1000));
      gsap.ticker.lagSmoothing(0);
      return;
    }
    const loop = (time) => {
      lenis.raf(time);
      requestAnimationFrame(loop);
    };
    requestAnimationFrame(loop);
  };
  if (document.readyState === 'complete') wire();
  else window.addEventListener('load', wire, { once: true });

  // Anchor links scroll through Lenis. A target's own scroll-margin-top
  // (header plus a sticky rail, for instance) wins; else the glass header.
  const offsetFor = (target) => {
    const margin = parseFloat(getComputedStyle(target).scrollMarginTop) || 0;
    const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--tg-header-height')) || 0;
    return -(margin > 0 ? margin : header);
  };

  document.addEventListener(
    'click',
    (event) => {
      const link = event.target instanceof Element ? event.target.closest('a[href*="#"]') : null;
      if (!link || link.hasAttribute('download') || link.target === '_blank') return;
      const url = new URL(link.href, location.href);
      if (url.pathname !== location.pathname || url.search !== location.search || url.hash.length < 2) return;
      const target = document.getElementById(decodeURIComponent(url.hash.slice(1)));
      if (!target) return;
      event.preventDefault();
      history.pushState(null, '', url.hash);
      lenis.scrollTo(target, { offset: offsetFor(target), duration: 1.4 });
    },
    { passive: false }
  );

  // Arriving with a hash: land under the header after layout settles.
  if (location.hash.length > 1) {
    const target = document.getElementById(decodeURIComponent(location.hash.slice(1)));
    if (target) {
      window.addEventListener(
        'load',
        () => lenis.scrollTo(target, { offset: offsetFor(target), immediate: true }),
        { once: true }
      );
    }
  }

  // Pause while the theme locks scroll for a drawer or dialog.
  const root = document.documentElement;
  const sync = () => (root.hasAttribute('scroll-lock') ? lenis.stop() : lenis.start());
  new MutationObserver(sync).observe(root, { attributes: true, attributeFilter: ['scroll-lock'] });
  sync();
})();
